import {
  afterNextRender,
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  type ElementRef,
  effect,
  inject,
  input,
  numberAttribute,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import {
  LucideBold,
  LucideHeading2,
  LucideHeading3,
  type LucideIconInput,
  LucideItalic,
  LucideLink,
  LucideList,
  LucideListOrdered,
  LucideQuote,
  LucideRemoveFormatting,
  LucideSquareCode,
  LucideStrikethrough,
  LucideUnderline,
} from '@lucide/angular';
import { MmscanButton } from '@mmscan/angular-ui/button';
import { cn } from '@mmscan/angular-ui/core';
import { MmscanValueControl } from '@mmscan/angular-ui/forms';
import { MmscanIcon } from '@mmscan/angular-ui/icon';
import { MmscanPopoverTrigger } from '@mmscan/angular-ui/popover';
import type QuillType from 'quill';
import { MMSCAN_EDITOR_I18N, type MmscanEditorI18n } from './editor-i18n';

export type MmscanEditorTool =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'strike'
  | 'heading2'
  | 'heading3'
  | 'orderedList'
  | 'bulletList'
  | 'blockquote'
  | 'codeBlock'
  | 'link'
  | 'clean';

export type MmscanEditorPreset = 'minimal' | 'standard' | 'full';

export const MMSCAN_EDITOR_PRESETS: Record<MmscanEditorPreset, readonly (MmscanEditorTool | '|')[]> = {
  minimal: ['bold', 'italic', 'underline', '|', 'bulletList', 'orderedList'],
  standard: [
    'heading2',
    'heading3',
    '|',
    'bold',
    'italic',
    'underline',
    '|',
    'bulletList',
    'orderedList',
    'blockquote',
    '|',
    'link',
    'clean',
  ],
  full: [
    'heading2',
    'heading3',
    '|',
    'bold',
    'italic',
    'underline',
    'strike',
    '|',
    'bulletList',
    'orderedList',
    'blockquote',
    'codeBlock',
    '|',
    'link',
    'clean',
  ],
};

interface ToolDefinition {
  icon: LucideIconInput;
  label: keyof MmscanEditorI18n;
  /** Quill format name and the value that means "on". */
  format: string;
  value: unknown;
}

const TOOLS: Record<Exclude<MmscanEditorTool, 'link' | 'clean'>, ToolDefinition> = {
  bold: { icon: LucideBold, label: 'bold', format: 'bold', value: true },
  italic: { icon: LucideItalic, label: 'italic', format: 'italic', value: true },
  underline: { icon: LucideUnderline, label: 'underline', format: 'underline', value: true },
  strike: { icon: LucideStrikethrough, label: 'strikethrough', format: 'strike', value: true },
  heading2: { icon: LucideHeading2, label: 'heading2', format: 'header', value: 2 },
  heading3: { icon: LucideHeading3, label: 'heading3', format: 'header', value: 3 },
  orderedList: { icon: LucideListOrdered, label: 'numberedList', format: 'list', value: 'ordered' },
  bulletList: { icon: LucideList, label: 'bulletList', format: 'list', value: 'bullet' },
  blockquote: { icon: LucideQuote, label: 'quote', format: 'blockquote', value: true },
  codeBlock: { icon: LucideSquareCode, label: 'codeBlock', format: 'code-block', value: true },
};

/** Quill formats allowed by each tool; anything else (images, colours, fonts) is stripped on paste. */
const FORMATS: Record<MmscanEditorTool, string[]> = {
  bold: ['bold'],
  italic: ['italic'],
  underline: ['underline'],
  strike: ['strike'],
  heading2: ['header'],
  heading3: ['header'],
  orderedList: ['list'],
  bulletList: ['list'],
  blockquote: ['blockquote'],
  codeBlock: ['code-block'],
  link: ['link'],
  clean: [],
};

let nextId = 0;

/**
 * Rich-text field built on Quill 2 (loaded on demand) with an accessible toolbar: a single tab
 * stop with arrow-key navigation, `aria-pressed` on toggles and translated labels. The value is
 * HTML (`''` when empty). Only the formats of the chosen tools are kept, so pasted images,
 * colours or scripts are dropped.
 *
 * Needs `quill` in the app and `@import '@mmscan/angular-editor/styles.css'` in its stylesheet.
 *
 * ```html
 * <mmscan-form-field label="Description">
 *   <mmscan-rich-text-editor formControlName="description" preset="standard" [maxLength]="2000" />
 * </mmscan-form-field>
 * ```
 */
@Component({
  selector: 'mmscan-rich-text-editor',
  imports: [MmscanIcon, MmscanButton, MmscanPopoverTrigger],
  template: `
    <div [class]="frameClasses()">
      @if (!readonly()) {
        <!-- eslint-disable-next-line @angular-eslint/template/interactive-supports-focus -- keys bubble from the roving-tabindex toolbar buttons -->
        <div
          role="toolbar"
          [attr.aria-label]="i18n().toolbar"
          [attr.aria-controls]="editorId"
          class="flex flex-wrap items-center gap-0.5 border-b border-border p-1"
          (keydown)="onToolbarKeydown($event)"
        >
          @for (tool of tools(); track $index; let index = $index) {
            @if (tool === '|') {
              <span role="separator" aria-orientation="vertical" class="mx-1 h-5 w-px bg-border"></span>
            } @else if (tool === 'link') {
              <button
                type="button"
                [class]="toolClass"
                [attr.aria-label]="i18n().link"
                [attr.aria-pressed]="!!activeFormats()['link']"
                [attr.tabindex]="index === toolFocus() ? 0 : -1"
                [disabled]="isDisabled()"
                [mmscanPopoverTrigger]="linkEditor"
                [popoverAriaLabel]="i18n().link"
                (mousedown)="$event.preventDefault()"
                (opened)="startLinkEdit()"
              >
                <mmscan-icon [icon]="linkIcon" size="sm" />
              </button>
            } @else if (tool === 'clean') {
              <button
                type="button"
                [class]="toolClass"
                [attr.aria-label]="i18n().clearFormatting"
                [attr.tabindex]="index === toolFocus() ? 0 : -1"
                [disabled]="isDisabled()"
                (mousedown)="$event.preventDefault()"
                (click)="clearFormatting()"
              >
                <mmscan-icon [icon]="cleanIcon" size="sm" />
              </button>
            } @else {
              <button
                type="button"
                [class]="toolClass"
                [attr.aria-label]="i18n()[definition(tool).label]"
                [attr.aria-pressed]="isActive(tool)"
                [attr.tabindex]="index === toolFocus() ? 0 : -1"
                [disabled]="isDisabled()"
                (mousedown)="$event.preventDefault()"
                (click)="toggle(tool)"
              >
                <mmscan-icon [icon]="definition(tool).icon" size="sm" />
              </button>
            }
          }
        </div>
      }
      <div #editor class="mmscan-editor" [style.min-height.rem]="minHeight()"></div>
    </div>
    @if (maxLength()) {
      <p class="mt-1 text-right text-xs text-muted-foreground" [class.text-danger-text]="length() >= maxLength()!">
        {{ characterCount() }}
      </p>
    }

    <ng-template #linkEditor let-popover>
      <form class="flex w-72 flex-col gap-2" (submit)="$event.preventDefault(); applyLink(url.value); popover.close()">
        <label [attr.for]="editorId + '-url'" class="text-sm font-medium">{{ i18n().linkUrl }}</label>
        <input
          #url
          type="url"
          [id]="editorId + '-url'"
          [value]="linkDraft()"
          placeholder="https://"
          class="w-full rounded-md border border-input bg-background px-field-x py-field-y text-sm focus-visible:outline-2 focus-visible:outline-ring"
        />
        <div class="flex justify-end gap-2">
          @if (activeFormats()['link']) {
            <button
              mmscanButton
              type="button"
              variant="ghost"
              severity="danger"
              size="sm"
              (click)="applyLink(''); popover.close()"
            >
              {{ i18n().removeLink }}
            </button>
          }
          <button mmscanButton type="submit" size="sm">{{ i18n().applyLink }}</button>
        </div>
      </form>
    </ng-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class MmscanRichTextEditor extends MmscanValueControl<string> {
  protected readonly emptyValue = '';
  protected readonly i18n = inject(MMSCAN_EDITOR_I18N);
  private readonly editorHost = viewChild.required<ElementRef<HTMLElement>>('editor');
  private quill?: QuillType;
  /** HTML last sent to the form, to avoid re-applying our own changes (and losing the caret). */
  private lastHtml = '';
  /** Selection before focus moved to the toolbar or the link popover. */
  private savedRange: { index: number; length: number } | null = null;

  protected readonly editorId = `mmscan-editor-${nextId++}`;
  protected readonly linkIcon = LucideLink;
  protected readonly cleanIcon = LucideRemoveFormatting;
  protected readonly toolClass =
    'inline-flex size-8 cursor-pointer items-center justify-center rounded-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground aria-pressed:bg-accent aria-pressed:text-foreground focus-visible:outline-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-50';

  readonly preset = input<MmscanEditorPreset>('standard');
  /** Custom toolbar, overriding `preset`; `'|'` adds a separator. */
  readonly toolbar = input<readonly (MmscanEditorTool | '|')[] | null>(null);
  readonly placeholder = input('');
  readonly readonly = input(false, { transform: booleanAttribute });
  /** Maximum characters of text (formatting excluded); extra input is cut. */
  readonly maxLength = input<number | null, unknown>(null, {
    transform: (value: unknown) => (value === null || value === undefined ? null : numberAttribute(value)),
  });
  /** Accessible name when the editor isn't inside a labelled `mmscan-form-field`. */
  readonly ariaLabel = input<string>();
  /** Minimum editing area height, in rem. */
  readonly minHeight = input(8, { transform: numberAttribute });

  protected readonly tools = computed(() => this.toolbar() ?? MMSCAN_EDITOR_PRESETS[this.preset()]);
  protected readonly activeFormats = signal<Record<string, unknown>>({});
  protected readonly length = signal(0);
  protected readonly linkDraft = signal('');
  /** Roving tab stop inside the toolbar. */
  protected readonly toolFocus = signal(0);

  protected readonly characterCount = computed(() =>
    this.i18n().characterCount.replace('{count}', String(this.length())).replace('{max}', String(this.maxLength()))
  );
  protected readonly frameClasses = computed(() =>
    cn(
      'overflow-hidden rounded-md border border-input bg-background text-sm focus-within:outline-2 focus-within:-outline-offset-1 focus-within:outline-ring',
      this.state.showError() && 'border-danger-text',
      this.isDisabled() && 'opacity-50'
    )
  );

  constructor() {
    super();
    // The editing area is a contenteditable div: labelled with aria-labelledby.
    this.field?.register(this.state, null);

    afterNextRender(() => void this.createEditor());

    // Form → editor.
    effect(() => {
      const html = this.current();
      untracked(() => {
        if (this.quill && html !== this.lastHtml) this.setHtml(html);
      });
    });
    effect(() => {
      const enabled = !this.isDisabled() && !this.readonly();
      untracked(() => this.quill?.enable(enabled));
    });
    // Accessibility attributes on Quill's editable root.
    effect(() => {
      const attributes = {
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-labelledby': this.ariaLabel() ? null : (this.field?.labelId ?? null),
        'aria-label': this.ariaLabel() ?? null,
        'aria-describedby': this.describedBy(),
        'aria-required': this.state.required() ? 'true' : null,
        'aria-invalid': this.state.showError() ? 'true' : null,
        'aria-readonly': this.readonly() ? 'true' : null,
        id: this.editorId,
      };
      untracked(() => this.applyRootAttributes(attributes));
    });

    inject(DestroyRef).onDestroy(() => this.quill?.off('text-change'));
  }

  protected definition(tool: MmscanEditorTool): ToolDefinition {
    return TOOLS[tool as keyof typeof TOOLS];
  }

  protected isActive(tool: MmscanEditorTool): boolean {
    const { format, value } = this.definition(tool);
    return this.activeFormats()[format] === value;
  }

  private async createEditor(): Promise<void> {
    const { default: Quill } = await import('quill');
    const formats = [...new Set(this.tools().flatMap((tool) => (tool === '|' ? [] : FORMATS[tool])))];
    // Headings can come from either heading tool; lists from either list tool.
    const quill = new Quill(this.editorHost().nativeElement, {
      formats,
      modules: { toolbar: false },
      placeholder: this.placeholder(),
      readOnly: this.readonly() || this.isDisabled(),
    });
    this.quill = quill;
    this.setHtml(this.current());

    quill.on('text-change', (_delta, _old, source) => {
      this.enforceMaxLength();
      this.length.set(Math.max(0, quill.getLength() - 1));
      this.refreshFormats();
      if (source !== 'user') return;
      const html = this.getHtml();
      this.lastHtml = html;
      this.commit(html);
    });
    quill.on('selection-change', (range) => {
      if (range) this.savedRange = range;
      else this.markTouched();
      this.refreshFormats();
    });
    this.length.set(Math.max(0, quill.getLength() - 1));
    this.applyRootAttributes({
      role: 'textbox',
      'aria-multiline': 'true',
      'aria-labelledby': this.ariaLabel() ? null : (this.field?.labelId ?? null),
      'aria-label': this.ariaLabel() ?? null,
      'aria-describedby': this.describedBy(),
      'aria-required': this.state.required() ? 'true' : null,
      'aria-invalid': this.state.showError() ? 'true' : null,
      'aria-readonly': this.readonly() ? 'true' : null,
      id: this.editorId,
    });
  }

  private applyRootAttributes(attributes: Record<string, string | null>): void {
    const root = this.quill?.root;
    if (!root) return;
    for (const [name, value] of Object.entries(attributes)) {
      if (value === null) root.removeAttribute(name);
      else root.setAttribute(name, value);
    }
  }

  private getHtml(): string {
    const quill = this.quill;
    if (!quill || quill.getLength() <= 1) return '';
    // Quill 2.0's getSemanticHTML() turns every space into &nbsp;; keep normal spaces.
    return quill.getSemanticHTML().replace(/&nbsp;/g, ' ');
  }

  private setHtml(html: string): void {
    const quill = this.quill;
    if (!quill) return;
    // `convert` runs the clipboard matchers, so only allowed formats survive.
    quill.setContents(quill.clipboard.convert({ html: html ?? '' }), 'silent');
    this.lastHtml = html ?? '';
    this.length.set(Math.max(0, quill.getLength() - 1));
    this.refreshFormats();
  }

  private enforceMaxLength(): void {
    const quill = this.quill;
    const max = this.maxLength();
    if (!quill || !max) return;
    const length = quill.getLength() - 1;
    if (length > max) quill.deleteText(max, length - max, 'silent');
  }

  private refreshFormats(): void {
    const range = this.quill?.getSelection() ?? this.savedRange;
    this.activeFormats.set(range && this.quill ? { ...this.quill.getFormat(range) } : {});
  }

  private range(): { index: number; length: number } {
    return this.quill?.getSelection() ?? this.savedRange ?? { index: this.quill?.getLength() ?? 0, length: 0 };
  }

  protected toggle(tool: MmscanEditorTool): void {
    const quill = this.quill;
    if (!quill) return;
    const { format, value } = this.definition(tool);
    const range = this.range();
    quill.setSelection(range, 'silent');
    quill.format(format, this.isActive(tool) ? false : value, 'user');
    this.refreshFormats();
  }

  protected clearFormatting(): void {
    const quill = this.quill;
    if (!quill) return;
    const range = this.range();
    quill.removeFormat(range.index, Math.max(range.length, 1), 'user');
    this.refreshFormats();
  }

  protected startLinkEdit(): void {
    const link = this.activeFormats()['link'];
    this.linkDraft.set(typeof link === 'string' ? link : '');
  }

  /** Adds, changes (`url`) or removes (`''`) the link on the saved selection. */
  protected applyLink(url: string): void {
    const quill = this.quill;
    if (!quill) return;
    const range = this.range();
    const href = url.trim();
    if (range.length === 0 && href) {
      quill.insertText(range.index, href, 'link', href, 'user');
      quill.setSelection(range.index + href.length, 0, 'silent');
    } else {
      quill.formatText(range.index, range.length, 'link', href || false, 'user');
    }
    this.refreshFormats();
    quill.focus();
  }

  /** WAI-ARIA toolbar: one tab stop, arrow keys move between buttons. */
  protected onToolbarKeydown(event: KeyboardEvent): void {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const toolbar = event.currentTarget as HTMLElement;
    const buttons = [...toolbar.querySelectorAll<HTMLButtonElement>('button:not(:disabled)')];
    const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? buttons.length - 1
          : (current + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length;
    event.preventDefault();
    const button = buttons[next];
    if (!button) return;
    const indices = this.tools()
      .map((tool, index) => (tool === '|' ? -1 : index))
      .filter((index) => index >= 0);
    this.toolFocus.set(indices[next] ?? 0);
    button.focus();
  }
}
